// REISOR_P19_DRIVER_MARKET_STATUS_LIFECYCLE_V1
import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Truck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { reisorProDriverRequest } from "../utils/reisorProMarketHttp.js";

const COPY = {
  ru: {
    myVehicle: "Моя машина",
    statusFree: "🟢 СВОБОДЕН",
    statusBusy: "🔴 ЗАНЯТ",
    statusUnknown: "⚪ НЕ УКАЗАНО",
    toggleToBusy: "Отметить машину занятой",
    toggleToFree: "Отметить машину свободной",
    saving: "Сохраняем…",
    profileRequired: "Заполните кузов и грузоподъёмность машины в профиле.",
    failed: "Не удалось изменить статус машины.",
  },
  uz: {
    myVehicle: "Менинг машинам",
    statusFree: "🟢 БЎШ",
    statusBusy: "🔴 БАНД",
    statusUnknown: "⚪ БЕЛГИЛАНМАГАН",
    toggleToBusy: "Машинани банд деб белгилаш",
    toggleToFree: "Машинани бўш деб белгилаш",
    saving: "Сақланмоқда…",
    profileRequired: "Профилда кузов тури ва юк кўтариш қувватини тўлдиринг.",
    failed: "Машина ҳолатини ўзгартириб бўлмади.",
  },
  tr: {
    myVehicle: "Aracım",
    statusFree: "🟢 MÜSAİT",
    statusBusy: "🔴 MEŞGUL",
    statusUnknown: "⚪ BELİRTİLMEDİ",
    toggleToBusy: "Aracı meşgul olarak işaretle",
    toggleToFree: "Aracı müsait olarak işaretle",
    saving: "Kaydediliyor…",
    profileRequired: "Profilde kasa tipi ve taşıma kapasitesini doldurun.",
    failed: "Araç durumu değiştirilemedi.",
  },
};

function token() {
  for (const storage of [localStorage, sessionStorage]) {
    for (const key of ["driver_token", "drv_token", "driverToken", "drvToken"]) {
      try { const value = String(storage.getItem(key) || "").trim(); if (value) return value; } catch {}
    }
  }
  return "";
}

function uiLang(i18n) {
  const raw = String(i18n?.resolvedLanguage || i18n?.language || "ru").toLowerCase();
  if (raw.startsWith("uz")) return "uz";
  if (raw.startsWith("tr")) return "tr";
  return "ru";
}

export default function DriverMarketStatusQuickBar() {
  const { i18n } = useTranslation();
  const copy = COPY[uiLang(i18n)] || COPY.ru;
  const [state, setState] = useState("UNKNOWN");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    const t = token(); if (!t) return;
    try {
      const { response, data } = await reisorProDriverRequest("/api/driver/available-vehicles/market-status", { token: t });
      if (!response?.ok || !data?.ok) return;
      const current = String(data?.status?.state || "UNKNOWN").toUpperCase();
      if (current !== "UNKNOWN") {
        setState(current);
        return;
      }

      // Compatibility self-heal for approved profiles created before P19.
      // UNKNOWN means the driver never explicitly chose BUSY, so the new default
      // policy promotes the vehicle to factual FREE when the driver opens the app.
      const promoted = await reisorProDriverRequest("/api/driver/available-vehicles/market-status", {
        token: t, method: "PATCH", body: { state: "FREE" },
      });
      if (promoted?.response?.ok && promoted?.data?.ok) {
        setState(String(promoted?.data?.status?.state || "FREE").toUpperCase());
      } else {
        setState("UNKNOWN");
      }
    } catch {}
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function set(next) {
    const t = token(); if (!t || busy) return;
    setBusy(true); setNotice("");
    try {
      const { response, data } = await reisorProDriverRequest("/api/driver/available-vehicles/market-status", {
        token: t, method: "PATCH", body: { state: next },
      });
      if (!response?.ok || !data?.ok) {
        if (data?.error === "FREE_VEHICLE_PROFILE_REQUIRED") setNotice(copy.profileRequired);
        else setNotice(copy.failed);
        return;
      }
      setState(String(data?.status?.state || next).toUpperCase());
      window.dispatchEvent(new CustomEvent("reisor:vehicle-market-status-changed", { detail: { state: next } }));
    } catch { setNotice(copy.failed); }
    finally { setBusy(false); }
  }

  const free = state === "FREE";
  const occupied = state === "BUSY";
  const nextState = free ? "BUSY" : "FREE";
  const statusLabel = free ? copy.statusFree : occupied ? copy.statusBusy : copy.statusUnknown;
  const toggleLabel = free ? copy.toggleToBusy : copy.toggleToFree;

  return (
    <section className="mb-4 rounded-3xl border border-slate-700 bg-slate-900/80 p-3" data-reisor-driver-quick-status="p22-single-toggle">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2 text-sm font-black text-white"><Truck size={18}/> {copy.myVehicle}</div>
        <button
          type="button"
          disabled={busy}
          onClick={() => void set(nextState)}
          aria-pressed={occupied}
          aria-label={toggleLabel}
          title={toggleLabel}
          data-reisor-driver-status-toggle="single"
          className={`min-h-[44px] shrink-0 rounded-xl px-2 text-sm font-black transition active:scale-[0.97] disabled:cursor-wait disabled:opacity-70 ${
            free
              ? "text-emerald-400"
              : occupied
                ? "text-rose-400"
                : "text-slate-400"
          }`}
        >
          <span className="flex items-center gap-2">
            {busy ? <Loader2 size={16} className="animate-spin" aria-hidden="true"/> : null}
            {busy ? copy.saving : statusLabel}
          </span>
        </button>
      </div>
      {notice ? <div className="mt-2 text-xs font-semibold text-rose-300" role="status">{notice}</div> : null}
    </section>
  );
}
